import React, { useState, useEffect } from "react";
import { useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Edit2, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const Services = () => {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    duration: 30,
    price: 0,
    description: "",
    category: "",
    is_active: true
  });

  const durations = [10, 15, 20, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 270, 300];
  const categories = [
    { value: "haircut", label: language === "it" ? "Taglio" : "Haircut" },
    { value: "color", label: language === "it" ? "Colore" : "Color" },
    { value: "treatment", label: language === "it" ? "Trattamento" : "Treatment" },
    { value: "styling", label: language === "it" ? "Styling" : "Styling" },
    { value: "beard", label: language === "it" ? "Barba" : "Beard" },
    { value: "spa", label: "Spa" },
    { value: "nails", label: language === "it" ? "Unghie" : "Nails" },
    { value: "makeup", label: "Makeup" },
    { value: "other", label: language === "it" ? "Altro" : "Other" }
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, { withCredentials: true });
      setServices(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingService) {
        await axios.patch(`${API}/services/${editingService.service_id}`, formData, {
          withCredentials: true
        });
        toast.success(language === "it" ? "Servizio aggiornato" : "Service updated");
      } else {
        await axios.post(`${API}/services`, formData, { withCredentials: true });
        toast.success(language === "it" ? "Servizio creato" : "Service created");
      }
      
      setDialogOpen(false);
      setEditingService(null);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      duration: service.duration,
      price: service.price,
      description: service.description || "",
      category: service.category || "",
      is_active: service.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm(language === "it" ? "Eliminare questo servizio?" : "Delete this service?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/services/${serviceId}`, { withCredentials: true });
      toast.success(language === "it" ? "Servizio eliminato" : "Service deleted");
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      duration: 30,
      price: 0,
      description: "",
      category: "",
      is_active: true
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif font-medium">{t("services")}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" ? "Gestisci i servizi del tuo salone" : "Manage your salon services"}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingService(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-lift" data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t("add")} {t("services").toLowerCase()}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingService 
                    ? (language === "it" ? "Modifica Servizio" : "Edit Service")
                    : (language === "it" ? "Nuovo Servizio" : "New Service")
                  }
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("name")} *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={language === "it" ? "Nome servizio" : "Service name"}
                    required
                    data-testid="service-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{t("duration")} *</label>
                    <Select
                      value={formData.duration.toString()}
                      onValueChange={(value) => setFormData({ ...formData, duration: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="service-duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {durations.map((d) => (
                          <SelectItem key={d} value={d.toString()}>
                            {d} {t("minutes")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1.5">{t("price")} (€) *</label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={formData.price.toString().replace('.', ',')}
                      onChange={(e) => {
                        // Accept both comma and dot as decimal separator
                        const value = e.target.value.replace(',', '.');
                        const numValue = parseFloat(value) || 0;
                        setFormData({ ...formData, price: numValue });
                      }}
                      placeholder="0,00"
                      required
                      data-testid="service-price-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("category")}</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="service-category-select">
                      <SelectValue placeholder={language === "it" ? "Seleziona categoria" : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Descrizione" : "Description"}
                  </label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={language === "it" ? "Descrizione opzionale" : "Optional description"}
                    data-testid="service-description-input"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm">
                    {language === "it" ? "Servizio attivo" : "Service active"}
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("cancel")}
                  </Button>
                  <Button type="submit" data-testid="save-service-btn">
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service) => (
            <div 
              key={service.service_id}
              className={`p-5 rounded-2xl border bg-card shadow-card card-hover ${
                !service.is_active ? "opacity-60" : ""
              }`}
              data-testid={`service-card-${service.service_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{service.name}</h3>
                  {service.category && (
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                      {categories.find(c => c.value === service.category)?.label || service.category}
                    </span>
                  )}
                </div>
                <span className="font-mono font-bold text-lg">€{service.price}</span>
              </div>
              
              {service.description && (
                <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
              )}
              
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  {service.duration} {t("minutes")}
                </span>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(service)}
                    data-testid={`edit-service-${service.service_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(service.service_id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-service-${service.service_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {services.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "it" ? "Nessun servizio. Aggiungi il primo!" : "No services. Add the first one!"}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Services;
